import React, { useState } from 'react';
import { Button, Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, Popover, PopoverContent, PopoverTrigger, } from '@cargomatic/ui';
import { Check, ChevronsUpDown, Loader2, Plus } from 'lucide-react';
import EquipmentTypeBadge from 'components/v2/EquipmentTypeBadge';
import { useSystemEquipmentSearch, } from '../hooks/useSystemEquipmentSearch';
const VALID_ASSET_TYPES = new Set([
    'container',
    'chassis',
    'trailer',
]);
export const EquipmentCombobox = ({ value, assetType, orderEquipment, disabled = false, onSelect, onCreate, }) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const orderEquipmentIds = orderEquipment.map((eq) => eq.equipment_id);
    const { results: systemResults, isLoading: isSystemLoading } = useSystemEquipmentSearch({
        query: search,
        assetType,
        excludeIds: orderEquipmentIds,
        enabled: open,
    });
    const filteredOrderEquipment = orderEquipment.filter((eq) => !search || eq.equipment_id.toLowerCase().includes(search.toLowerCase()));
    const hasOrderResults = filteredOrderEquipment.length > 0;
    const hasSystemResults = systemResults.length > 0;
    const trimmedSearch = search.trim();
    const showCreate = !!onCreate &&
        !hasOrderResults &&
        !hasSystemResults &&
        !isSystemLoading &&
        !!trimmedSearch;
    const handleSelect = (item) => {
        var _a;
        const normalized = (_a = item._equipmentType) === null || _a === void 0 ? void 0 : _a.toLowerCase();
        onSelect({
            _id: item._id,
            equipment_id: item.equipment_id,
            _equipmentType: VALID_ASSET_TYPES.has(normalized !== null && normalized !== void 0 ? normalized : '')
                ? normalized
                : undefined,
        });
        setOpen(false);
        setSearch('');
    };
    const handleCreate = () => {
        if (!onCreate)
            return;
        onCreate(trimmedSearch);
        setOpen(false);
        setSearch('');
    };
    // Build a single, contiguous list of selectable item ids so that
    // downshift (used internally by Command) can register every item by index.
    // Order Equipment ids come first, then System Equipment ids.
    const allItemIds = [
        ...filteredOrderEquipment.map((eq) => eq.equipment_id),
        ...systemResults.map((eq) => eq.equipment_id),
    ];
    const renderRow = (item, keyPrefix, itemIndex) => {
        return (React.createElement(CommandItem, { key: `${keyPrefix}-${item.equipment_id}`, index: itemIndex, item: item.equipment_id },
            React.createElement("div", { className: "flex items-center gap-2", onMouseDown: (e) => {
                    e.preventDefault();
                    handleSelect(item);
                } },
                React.createElement(Check, { className: `h-4 w-4 ${value === item.equipment_id ? 'opacity-100' : 'opacity-0'}` }),
                React.createElement("span", null, item.equipment_id),
                item._equipmentType && (React.createElement(EquipmentTypeBadge, { equipmentType: item._equipmentType.toLowerCase(), className: "text-xs" })))));
    };
    return (React.createElement(Popover
    // modal opts the popover out of the parent Dialog's pointer-events
    // lock so wheel scrolling works inside the list.
    // See https://github.com/dip/cmdk/issues/272
    , { 
        // modal opts the popover out of the parent Dialog's pointer-events
        // lock so wheel scrolling works inside the list.
        // See https://github.com/dip/cmdk/issues/272
        modal: true, open: open && !disabled, onOpenChange: (next) => {
            if (disabled)
                return;
            setOpen(next);
            if (!next)
                setSearch('');
        } },
        React.createElement(PopoverTrigger, { asChild: true },
            React.createElement(Button, { type: "button", variant: "outline", role: "combobox", "aria-expanded": open && !disabled, className: "w-full justify-between font-normal bg-transparent", disabled: disabled },
                React.createElement("span", { className: value ? '' : 'text-muted-foreground' }, value || 'Select equipment...'),
                React.createElement(ChevronsUpDown, { className: "ml-2 h-4 w-4 shrink-0 opacity-50" }))),
        React.createElement(PopoverContent, { className: "w-[400px] p-0", align: "start", sideOffset: 4, avoidCollisions: true, collisionPadding: 16 },
            React.createElement(Command, { useExternalFiltering: true, inputValue: search, onInputValueChange: setSearch, filteredItems: allItemIds, isLoading: isSystemLoading },
                React.createElement(CommandInput, { placeholder: "Search equipment numbers...", className: "h-9" }),
                React.createElement(CommandList, { className: "max-h-[360px]" },
                    hasOrderResults && (React.createElement(CommandGroup, { heading: "Order Equipment", className: "sticky top-0 z-10 bg-popover border-b" }, filteredOrderEquipment.map((eq, i) => renderRow(eq, 'order', i)))),
                    isSystemLoading && (React.createElement("div", { className: "flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground" },
                        React.createElement(Loader2, { className: "h-3 w-3 animate-spin" }),
                        "Searching system equipment...")),
                    hasSystemResults && (React.createElement(CommandGroup, { heading: "System Equipment" }, systemResults.map((eq, i) => renderRow(eq, 'system', filteredOrderEquipment.length + i)))),
                    showCreate && (React.createElement(CommandEmpty, null,
                        React.createElement("div", { className: "px-2" },
                            React.createElement(Button, { type: "button", size: "sm", variant: "secondary", className: "w-full justify-start", onClick: handleCreate },
                                React.createElement(Plus, { className: "mr-2 h-4 w-4" }),
                                "Create \"",
                                trimmedSearch,
                                "\"")))))))));
};
